import { DetailViewModel } from "@frui.ts/datascreens/dist";
import { bound } from "@frui.ts/helpers/dist";
import { Router, watchBusy } from "@frui.ts/screens/dist";
import { attachAutomaticValidator, getValidationMessage, validateAll } from "@frui.ts/validation/dist";
import type { interfaces } from "inversify";
import cloneDeep from "lodash/cloneDeep";
import toUpper from "lodash/toUpper";
import { computed, observable, reaction, toJS } from "mobx";
import OptionsService from "services/optionsService";
import { Modules, editationNavigationName } from "config";
import type LicensePlate from "entities/licensePlate";
import { LicensePlateValidationRules } from "entities/licensePlate";
import ExemptionRepository from "repositories/exemptionRepository";
import EnumService from "services/enumService";
import EventBus, { exemptionUpdated } from "services/eventBus";
import LocalizationService from "services/localizationService";
import NotificationService, { NotificationType } from "services/notificationService";
import { czechTodayDate, endOfCzechDay, startOfCzechDay } from "utils/dateUtils";
import formatCountryEnumName from "utils/formatCountryEnumName";
import CountrySelectViewModel from "viewModels/countrySelectViewModel";
import * as Sentry from "@sentry/react";
import ExemptionChangeRequest, { UpdateExemptionValidationRules } from "entities/exemptionChangeRequest";
import ExemptionChangePreviewResponse from "entities/exemptionChangePreviewResponse";
import ExemptionChangeRejectionReason from "entities/exemptionChangeRejectionReason";
import { addMinutes, isAfter, isSameDay } from "date-fns";
import type VehicleRegisterVehicleClassifiedFuelType from "entities/vehicleRegisterVehicleClassifiedFuelType";
import { emptyValue } from "utils/emptyValue";

@Router.registerRoute({ route: editationNavigationName, name: Router.Self })
export default class EditExemptionViewModel extends DetailViewModel<ExemptionChangeRequest> {
  private disposers: Array<() => void> = [];
  private editExemptionPreview: ExemptionChangePreviewResponse;
  countrySelectViewModel: CountrySelectViewModel;
  @observable editStep = 1;
  @observable loading = false;
  @observable validUntil: Date | undefined;
  @observable validSince: Date;
  @observable isExemptionEditable: boolean;

  constructor(
    private exemptionChange: ExemptionChangeRequest,
    private repository: ExemptionRepository,
    private localizationService: LocalizationService,
    private eventBus: EventBus,
    private notificationService: NotificationService,
    private optionsService: OptionsService,
    private enumService: EnumService,
    private countrySelectViewModelFactory: ReturnType<typeof CountrySelectViewModel.Factory>
  ) {
    super();
    this.navigationName = editationNavigationName;
    this.countrySelectViewModel = this.countrySelectViewModelFactory("id");
  }

  onInitialize(): Promise<void> {
    this.disposers.push(reaction(() => this.item?.isImmediateCancellation, this.onImmediateCancellationChange));
    this.disposers.push(reaction(() => [this.item?.validUntil, this.item?.validSince], this.onValidDateChange));
    return super.onInitialize();
  }

  protected onActivate() {
    this.isExemptionEditable = addMinutes(this.item._originalValidSince, 15) >= new Date();
  }
  protected onDeactivate(isClosing: boolean): Promise<any> | void {
    if (isClosing) {
      this.disposers.forEach(dispose => dispose());
      this.disposers = [];
    }

    return super.onDeactivate(isClosing);
  }

  protected loadDetail() {
    const item = new ExemptionChangeRequest(this.exemptionChange);
    item.exemptionReasonId = this.getExemptionReasonId(item.exemptionReasonId);

    attachAutomaticValidator(item, UpdateExemptionValidationRules);
    attachAutomaticValidator(item.vehicle, LicensePlateValidationRules);

    return item;
  }

  @computed
  get officialRecordId() {
    return this.item.officialRecordId ?? "";
  }

  set officialRecordId(recordNumber: string) {
    this.item.officialRecordId = toUpper(recordNumber);
  }

  @computed
  get isCancellationReasonDisabled() {
    return !(this.item.validUntil && isAfter(this.item.validUntil, this.item.validSince));
  }
  @computed
  get isDisabled() {
    return !this.isExemptionEditable || this.isImmediateCancellation;
  }

  @computed
  get isImmediateCancellation() {
    return this.item.isImmediateCancellation ?? false;
  }

  @computed
  get officialRecordIdError() {
    return getValidationMessage(this.item, "officialRecordId");
  }

  @computed
  get exemptionPreview() {
    return this.editExemptionPreview;
  }

  @computed
  get isValidUntilDisabled() {
    return this.item._originalValidUntil && this.item._originalValidUntil < czechTodayDate();
  }

  @bound
  isContinueEnabled() {
    return this.editExemptionPreview?.canBeChanged === false;
  }

  @computed
  get countries() {
    return this.enumService.values("countries").map(country => ({ id: country.id, label: formatCountryEnumName(country) }));
  }

  @computed
  get exemptionReasonOptions() {
    return this.optionsService.enumOptions("notifiable_exemption_reasons", (item: any) => ({
      id: item.id,
      label: item.description,
    }));
  }

  @bound
  getExemptionReasonId(exemptionReason: string) {
    const exemptionReasons = this.enumService.values("notifiable_exemption_reasons");
    return exemptionReasons.find(x => x.id === exemptionReason)?.id ?? "";
  }

  @bound
  getExemptionReason(id: string) {
    const exemptionReasons = this.enumService.values("notifiable_exemption_reasons");
    return exemptionReasons.find(x => x.id === id)?.description ?? "";
  }

  @bound
  onImmediateCancellationChange() {
    if (this.item.isImmediateCancellation === true) {
      this.item.validUntil = new Date();
    } else {
      this.item.validUntil = this.validUntil;
    }
  }
  @bound
  onValidDateChange() {
    if (!this.item.isImmediateCancellation) {
      this.validUntil = this.item.validUntil;
    }
  }

  @bound
  @watchBusy
  onBack() {
    this.editStep = 1;
    this.editExemptionPreview = new ExemptionChangePreviewResponse();
  }

  @bound
  @watchBusy
  // eslint-disable-next-line sonarjs/cognitive-complexity
  async onSave() {
    if (
      this.isExemptionEditable &&
      !this.isImmediateCancellation &&
      this.item.validSince &&
      !isSameDay(this.item._originalValidSince, this.item.validSince)
    ) {
      this.item.validSince = startOfCzechDay(this.item.validSince);
    }
    if (!this.isImmediateCancellation && !this.isValidUntilDisabled && this.item.validUntil) {
      this.item.validUntil = endOfCzechDay(this.item.validUntil);
    }
    if (
      this.isValidUntilDisabled &&
      this.item.validUntil &&
      this.item._originalValidUntil &&
      isSameDay(this.item._originalValidUntil, this.item.validUntil)
    ) {
      this.item.validUntil = this.item._originalValidUntil;
    }
    const requestObj = cloneDeep(toJS(this.item));
    if (this.isImmediateCancellation && this.item.validUntil) {
      requestObj.validUntil = undefined;
    }
    if ((this.isExemptionEditable || this.isValidUntilDisabled) && this.item.exemptionReasonId) {
      requestObj.exemptionReasonId = this.getExemptionReasonId(this.item.exemptionReasonId) ?? "";
    }
    if (!requestObj.cancellationRecordId) {
      requestObj.cancellationRecordId = undefined;
    }
    if (!requestObj.cancellationReason) {
      requestObj.cancellationReason = undefined;
    }
    if (requestObj.cancellationReason && !this.item.validUntil) {
      requestObj.cancellationReason = undefined;
    }

    if (validateAll([this.item, this.item.vehicle]) && this.editStep === 2 && requestObj.exemptionId) {
      await this.update(requestObj.exemptionId, requestObj);

      return;
    }

    if (validateAll([this.item, this.item.vehicle]) && this.editStep === 1 && requestObj.exemptionId) {
      await this.updatePreview(requestObj.exemptionId, requestObj);
    }
  }

  @bound
  async update(exemptionId: string, exemption: ExemptionChangeRequest) {
    this.loading = true;

    try {
      await this.repository.update(exemptionId, exemption);
      this.eventBus.publish(exemptionUpdated({ entity: exemption }));
      this.notificationService.add({ title: this.translate("updated"), type: NotificationType.SUCCESS });
    } catch (e) {
      Sentry.captureException(e);
    }

    this.editStep = 1;
    this.loading = false;
    this.requestClose();
  }

  @bound
  async updatePreview(exemptionId: string, exemption: ExemptionChangeRequest) {
    this.loading = true;

    try {
      this.editExemptionPreview = await this.repository.updatePreview(exemptionId, exemption);
      this.editStep = 2;
    } catch (e) {
      Sentry.captureException(e);
    }

    this.loading = false;
  }

  @bound
  onClose() {
    this.requestClose();
  }

  @computed
  get rejectedReasonText() {
    if (!this.exemptionPreview?.rejectionReason) {
      return undefined;
    }

    const reason = this.exemptionPreview?.rejectionReason;

    if (reason === ExemptionChangeRejectionReason.OverlappingExemptionExists) {
      return this.translatePreview("overlappingExemptionExists");
    }
    return this.translatePreview("warningBypassingForbidden");
  }

  @computed
  get rejectedReason() {
    return !!this.rejectedReasonText;
  }

  @computed
  get warningReasons() {
    if (!this.exemptionPreview?.warningReasons) {
      return undefined;
    }
    return this.exemptionPreview?.warningReasons;
  }

  translatePreview(code: string, size?: number) {
    return this.localizationService.translateModule(Modules.callCenter, `editExemption.${code}`, size);
  }

  translate(code: string, size?: number) {
    return this.localizationService.translateModule(Modules.callCenter, `editExemption.${code}`, size);
  }

  translateGeneral(code: string, size?: number) {
    return this.localizationService.translateGeneral(code, size);
  }

  @bound
  translateCountryId(countryId: string) {
    if (!countryId) {
      return emptyValue;
    }
    const country = this.enumService.value("countries", countryId);

    if (!country) {
      return emptyValue;
    }
    return `${country.code} - ${country.name}`;
  }

  translateAttribute(model: "exemption", attribute: keyof ExemptionChangeRequest, size?: number): string;
  translateAttribute(model: "licensePlate", attribute: keyof LicensePlate, size?: number): string;
  translateAttribute(
    model: "exemption" | "licensePlate",
    attribute: keyof ExemptionChangeRequest | keyof LicensePlate,
    size?: number
  ) {
    return this.localizationService.translateAttribute(model, attribute, size);
  }

  translateClassifiedFuelType(classifiedFuelType: VehicleRegisterVehicleClassifiedFuelType) {
    return this.localizationService.translateEnum("vehicleRegisterVehicleClassifiedFuelType", classifiedFuelType);
  }

  static Factory({ container }: interfaces.Context) {
    return (exemptionChange: ExemptionChangeRequest) =>
      new EditExemptionViewModel(
        exemptionChange,
        container.get(ExemptionRepository),
        container.get(LocalizationService),
        container.get(EventBus),
        container.get(NotificationService),
        container.get(OptionsService),
        container.get(EnumService),
        container.get(CountrySelectViewModel.Factory)
      );
  }
}
